import React, { useState } from 'react';
import { Flex, Modal, Form, Input, Select } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Pencil, User, Phone, MapPin, Package } from 'lucide-react';
import { TableAction } from '@/components';
import { useResendStore } from '@/stores/useResendStore';
import { InventoryOrder, ResendPayload } from '@/types/inventory';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';

interface EditCustomerForm {
  full_name: string;
  phone: string;
  secondary_phone: string;
  province: string;
  region_uid: string;
  address_text: string;
  description?: string;
  // O-22: Dynamic product price fields
  [key: `product_price_${number}`]: number | string | undefined;
}

interface ResendRow {
  key: string;
  uid: string;
  platform_code: string;
  full_name: string;
  phone: string;
  address_text: string;
  products: InventoryOrder['products'];
}

/** Parse additional_phones — API may return a JSON string, an array, or null */
const parseSecondaryPhone = (phones: unknown): string => {
  if (!phones) return '';
  if (typeof phones === 'string') {
    try {
      const parsed = JSON.parse(phones);
      return Array.isArray(parsed) && parsed.length > 0 ? String(parsed[0]) : '';
    } catch {
      return '';
    }
  }
  if (Array.isArray(phones) && phones.length > 0) return String(phones[0]);
  return '';
};

const ResendStep1: React.FC = () => {
  const { t } = useTranslation();
  const { selectedOrders, orderOverrides, setOrderOverride } = useResendStore();
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>('');
  const [form] = Form.useForm<EditCustomerForm>();

  const { data: provinceList } = useFetchListProvinces();
  const { data: areaList } = useFetchAreaByProvinceCode(selectedProvinceCode);

  const filterOptions = (input: string, option: any) => {
    const label = option.children ?? '';
    return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const tableData: ResendRow[] = selectedOrders.map((order) => {
    const override = orderOverrides[order.uid];
    return {
      key: order.uid,
      uid: order.uid,
      platform_code: order.platform_code,
      full_name: override?.customer_data?.full_name ?? order.full_name,
      phone: override?.customer_data?.phone ?? order.phone,
      address_text: override?.customer_data?.address_text ?? order.address_text,
      products: order.products,
    };
  });

  // O-22: Track editing order's products for price editing
  const [editingProducts, setEditingProducts] = useState<Array<{ title?: string; price: number; quantity?: number }>>([]);

  const handleEditOpen = (record: ResendRow) => {
    const order = selectedOrders.find((o) => o.uid === record.uid);
    const override = orderOverrides[record.uid];

    // Use previously edited province/region if available, otherwise fall back to original order data
    const provinceCode = override?.customer_data?.province_code ?? order?.region?.province_code ?? '';
    const regionUid = override?.customer_data?.region_uid ?? order?.region?.uid ?? '';
    setSelectedProvinceCode(provinceCode);

    // O-22: Populate product prices (use override if available, else original)
    const products = override?.products ?? (order?.products ?? []).map((p) => ({
      title: p.title,
      price: Number(p.price) || 0,
      quantity: Number(p.quantity) || 1,
    }));
    setEditingProducts(products);

    const productFields: Record<string, number> = {};
    products.forEach((p, i) => {
      productFields[`product_price_${i}`] = p.price;
    });

    form.setFieldsValue({
      full_name: record.full_name,
      phone: record.phone,
      secondary_phone: override?.customer_data?.secondary_phone ?? parseSecondaryPhone(order?.additional_phones),
      province: provinceCode,
      region_uid: regionUid,
      address_text: record.address_text,
      // FE/BE-9: seed the delivery-instructions field from the override, then
      // fall back to the parent order's existing description.
      description: override?.customer_data?.description ?? order?.description ?? '',
      ...productFields,
    });

    setEditingUid(record.uid);
  };

  const handleProvinceChange = (provinceCode: string) => {
    setSelectedProvinceCode(provinceCode);
    form.setFieldsValue({ region_uid: '' });
  };

  const handleEditSave = () => {
    form.validateFields().then((values) => {
      if (!editingUid) return;
      const existing: ResendPayload = orderOverrides[editingUid] ?? {};

      const customerData: ResendPayload['customer_data'] = {
        ...existing.customer_data,
        full_name: values.full_name,
        phone: values.phone,
        address_text: values.address_text,
        // FE/BE-9: persist the delivery-instructions edit so the resend API
        // call carries it through as customer_data.description.
        description: values.description ?? existing.customer_data?.description,
      };

      if (values.secondary_phone) {
        customerData.secondary_phone = values.secondary_phone;
      }

      customerData.region_uid = values.region_uid || undefined;
      customerData.province_code = selectedProvinceCode || undefined;

      // O-22: Collect edited product prices
      const updatedProducts = editingProducts.map((p, i) => ({
        title: p.title,
        price: Number(values[`product_price_${i}`] ?? p.price),
        quantity: p.quantity,
      }));

      setOrderOverride(editingUid, {
        ...existing,
        customer_data: customerData,
        products: updatedProducts,
      });
      setEditingUid(null);
    });
  };

  const columns: ColumnsType<ResendRow> = [
    {
      title: String(t('platform_code')),
      dataIndex: 'platform_code',
      key: 'platform_code',
      render: (text: string) => (
        <span className='text-sm font-medium text-sd-foreground'>{text || '—'}</span>
      ),
    },
    {
      title: String(t('customer')),
      key: 'customer',
      render: (_: unknown, record: ResendRow) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.full_name}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-sd-muted-foreground shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-sd-muted-foreground'>{record.phone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: String(t('customer_address')),
      key: 'address_text',
      render: (_: unknown, record: ResendRow) => (
        <Flex align='center' gap={6}>
          <MapPin size={14} className='text-red-500 shrink-0' strokeWidth={1.5} />
          <span className='text-sm text-gray-600'>{record.address_text || '—'}</span>
        </Flex>
      ),
    },
    {
      title: String(t('product')),
      key: 'products',
      render: (_: unknown, record: ResendRow) => {
        const products = record.products ?? [];
        if (products.length === 0) {
          return <span className='text-xs text-gray-400'>{String(t('no_products'))}</span>;
        }
        return (
          <Flex align='center' gap={6}>
            <Package size={14} className='text-orange-500 shrink-0' strokeWidth={1.5} />
            <Flex vertical gap={2}>
              {products.map((p, idx) => (
                <Flex key={idx} align='center' gap={6}>
                  <span className='text-sm text-sd-foreground'>{p.title}</span>
                  <span className='text-xs text-gray-400'>x{p.quantity}</span>
                </Flex>
              ))}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: String(t('actions')),
      key: 'actions',
      fixed: 'right' as const,
      width: 80,
      render: (_: unknown, record: ResendRow) => (
        <button
          type='button'
          className='flex items-center justify-center w-8 h-8 rounded-lg text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          onClick={() => handleEditOpen(record)}
          title={String(t('edit_customer'))}
        >
          <Pencil size={16} strokeWidth={1.5} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className='border border-solid border-[rgb(232,230,227)] rounded-2xl bg-white p-6'>
        <h2 className='text-base font-semibold text-sd-foreground mt-0 mb-1'>
          {String(t('resend_step1_title'))}
        </h2>
        <p className='text-sm text-sd-muted-foreground mt-0 mb-4'>
          {String(t('resend_step1_subtitle'))}
        </p>

        <TableAction<ResendRow>
          columns={columns}
          data={tableData}
          pageSize={tableData.length || 10}
          hidePagination
          customClassName='import-table'
        />
      </div>

      <Modal
        className='sd-font'
        title={
          <div className='text-base font-semibold text-sd-foreground'>
            {String(t('edit_order_details', { defaultValue: 'Edit Order Details' }))}
          </div>
        }
        open={!!editingUid}
        onCancel={() => setEditingUid(null)}
        centered
        destroyOnClose
        footer={
          <div className='flex items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={() => setEditingUid(null)}
              className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              {String(t('cancel'))}
            </button>
            <button
              type='button'
              onClick={handleEditSave}
              className='h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
            >
              {String(t('save_changes', { defaultValue: 'Save Changes' }))}
            </button>
          </div>
        }
        styles={{
          content: { borderRadius: '16px', padding: '24px' },
          header: { borderBottom: '1px solid rgb(232,230,227)', paddingBottom: '16px', marginBottom: '16px' },
          mask: { backgroundColor: 'rgba(0,0,0,0.5)' },
        }}
      >
        <Form form={form} layout='vertical' className='pt-1'>
          {/* Contact section */}
          <div className='flex items-center gap-2 mb-3'>
            <User size={14} className='text-sd-muted-foreground' strokeWidth={1.5} />
            <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
              {String(t('contact_info'))}
            </span>
          </div>
          <Form.Item
            name='full_name'
            label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('full_name'))}</span>}
            rules={[{ required: true, message: String(t('required')) }]}
            className='mb-3'
          >
            <Input className='h-10 rounded-lg border-sd-input' placeholder={String(t('full_name'))} />
          </Form.Item>
          <div className='grid grid-cols-2 gap-3'>
            <Form.Item
              name='phone'
              label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('phone_number'))}</span>}
              rules={[{ required: true, message: String(t('required')) }]}
              className='mb-3'
            >
              <Input className='h-10 rounded-lg border-sd-input' placeholder={String(t('phone_number'))} />
            </Form.Item>
            <Form.Item
              name='secondary_phone'
              label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('secondary_phone'))}</span>}
              className='mb-3'
            >
              <Input className='h-10 rounded-lg border-sd-input' placeholder={String(t('secondary_phone'))} />
            </Form.Item>
          </div>

          {/* Location section */}
          <div className='flex items-center gap-2 mb-3 mt-2'>
            <MapPin size={14} className='text-sd-muted-foreground' strokeWidth={1.5} />
            <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
              {String(t('location', { defaultValue: 'Location' }))}
            </span>
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <Form.Item
              name='province'
              label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('region'))}</span>}
              className='mb-3'
            >
              <Select
                showSearch
                filterOption={filterOptions}
                placeholder={String(t('select_region'))}
                onChange={handleProvinceChange}
                allowClear
                className='h-10 [&_.ant-select-selector]:!rounded-lg'
              >
                {provinceList?.object?.map((item) => (
                  <Select.Option key={item.province_code} value={item.province_code}>
                    {item.province}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='region_uid'
              label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('area'))}</span>}
              className='mb-3'
            >
              <Select
                showSearch
                filterOption={filterOptions}
                placeholder={String(t('select_area'))}
                disabled={!selectedProvinceCode}
                allowClear
                className='h-10 [&_.ant-select-selector]:!rounded-lg'
              >
                {areaList?.object?.map((item) => (
                  <Select.Option key={item.uid} value={item.uid}>
                    {item.area_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <Form.Item
            name='address_text'
            label={<span className='text-[12px] text-sd-muted-foreground'>{String(t('address'))}</span>}
            className='mb-3'
          >
            <Input.TextArea className='rounded-lg border-sd-input' rows={2} placeholder={String(t('address'))} />
          </Form.Item>

          {/* FE/BE-9: Allow merchants to update delivery instructions during
              resend. The note flows through to the new child order so drivers
              see updated guidance on the second delivery attempt. */}
          <Form.Item
            name='description'
            label={
              <span className='text-[12px] text-sd-muted-foreground'>
                {String(t('delivery_instructions', { defaultValue: 'Delivery instructions' }))}
              </span>
            }
            className='mb-3'
          >
            <Input.TextArea
              className='rounded-lg border-sd-input'
              rows={2}
              maxLength={1000}
              placeholder={String(
                t('delivery_instructions_placeholder', {
                  defaultValue: 'Add any updates the driver should know (e.g. call on arrival, use side entrance)',
                }),
              )}
            />
          </Form.Item>

          {/* O-22: Product price editing */}
          {editingProducts.length > 0 && (
            <div className='mt-4'>
              <div className='flex items-center gap-2 mb-3'>
                <Package size={14} className='text-sd-muted-foreground' strokeWidth={1.5} />
                <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                  {String(t('products'))}
                </span>
              </div>
              <div className='rounded-xl border border-solid border-sd-border overflow-hidden'>
                {editingProducts.map((product, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between px-4 py-3 ${
                      index < editingProducts.length - 1 ? 'border-b border-solid border-sd-border' : ''
                    }`}
                  >
                    <div className='flex items-center gap-3 flex-1 min-w-0'>
                      <div className='h-8 w-8 rounded-lg bg-sd-muted/30 flex items-center justify-center shrink-0'>
                        <Package size={14} className='text-sd-muted-foreground' />
                      </div>
                      <div className='min-w-0'>
                        <span className='text-[13px] font-medium text-sd-foreground block truncate'>
                          {product.title || `${String(t('product'))} ${index + 1}`}
                        </span>
                        {product.quantity && product.quantity > 1 && (
                          <span className='text-[11px] text-sd-muted-foreground'>
                            {String(t('quantity'))}: {product.quantity}
                          </span>
                        )}
                      </div>
                    </div>
                    <Form.Item
                      name={`product_price_${index}`}
                      className='mb-0'
                      rules={[{ required: true, message: String(t('required')) }]}
                    >
                      <Input
                        type='number'
                        min={0}
                        className='h-9 w-[130px] rounded-lg border-sd-input text-end'
                        suffix={<span className='text-[11px] text-sd-muted-foreground'>{String(t('iqd'))}</span>}
                      />
                    </Form.Item>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default ResendStep1;
